/**
 * Session Storage Utility
 * Manage data caching with session storage to reduce backend load
 */

const STORAGE_KEYS = {
  CATEGORIES: "app_categories",
  PROJECTS: "app_projects",
  PROJECT_NAMES: "app_project_names",
  USERS: "app_users",
};

const CACHE_DURATION = {
  CATEGORIES: 60 * 60 * 1000, // 1 hour (categories rarely change)
  PROJECTS: 15 * 60 * 1000, // 15 minutes
  PROJECT_NAMES: 30 * 60 * 1000, // 30 minutes
  USERS: 10 * 60 * 1000, // 10 minutes
};

/**
 * Save data to session storage with timestamp
 */
export const saveToSessionStorage = (key, data) => {
  try {
    const item = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error("Error saving to session storage:", error);
  }
};

/**
 * Get data from session storage if not expired
 */
export const getFromSessionStorage = (key, maxAge = 5 * 60 * 1000) => {
  try {
    const item = sessionStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);
    const age = Date.now() - parsed.timestamp;

    // Check if cache expired
    if (age > maxAge) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error("Error reading from session storage:", error);
    return null;
  }
};

/**
 * Clear specific key from session storage
 */
export const clearFromSessionStorage = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing session storage:", error);
  }
};

/**
 * Clear all app data from session storage
 */
export const clearAllSessionStorage = () => {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.error("Error clearing all session storage:", error);
  }
};

/**
 * Invalidate cache when data changes (after create/update/delete)
 */
export const invalidateCache = (keys) => {
  const keysArray = Array.isArray(keys) ? keys : [keys];
  keysArray.forEach((key) => clearFromSessionStorage(key));
};

// Export storage keys and durations for consistency
export { STORAGE_KEYS, CACHE_DURATION };
